import React from "react";
import { Schedule, PlanningClassData } from "../../../../../types/planningTypes";
import { timeToMinutes, minutesToTime } from "../../utils/timeUtils";
import { getSubjectCodeFromkey } from "utils/schoolUtils";

interface CellContentProps {
  cls: PlanningClassData;
  day: { label: string; value: string };
  slot: { start: string; end: string };
  schedules: Schedule[];
  handleCellClick: (data: {
    type: "add" | "edit";
    schedule?: Schedule;
    classId: string;
    dayOfTheWeek: string;
    startTime: string;
    endTime: string;
  }) => void;
}

const CellContent: React.FC<CellContentProps> = ({
  cls,
  day,
  slot,
  schedules,
  handleCellClick,
}) => {
  const totalSlotMinutes = timeToMinutes(slot.end) - timeToMinutes(slot.start);
  const halfSlotMinutes = totalSlotMinutes / 2;

  const leftHalfStart = slot.start;
  const leftHalfEnd = minutesToTime(timeToMinutes(slot.start) + halfSlotMinutes);
  const rightHalfStart = leftHalfEnd;
  const rightHalfEnd = slot.end;

  // Read code & color directly from the Schedule.subject
  const getSubjectInfo = (sched: Schedule) => ({
    subjectCode: getSubjectCodeFromkey(sched.classSubject?.subject?.code || "N/A"),
    colorCode: sched.classSubject?.subject?.colorCode,
  });

  const leftSchedule = schedules.find(
    (sched) =>
      sched.classSubjectId &&
      cls.classSubject.some((cs) => cs.id === sched.classSubjectId) &&
      sched.dayOfTheWeek === day.value &&
      sched.startTime < leftHalfEnd &&
      sched.endTime > leftHalfStart
  );

  const rightSchedule = schedules.find(
    (sched) =>
      sched.classSubjectId &&
      cls.classSubject.some((cs) => cs.id === sched.classSubjectId) &&
      sched.dayOfTheWeek === day.value &&
      sched.startTime < rightHalfEnd &&
      sched.endTime > rightHalfStart
  );

  // Merged schedule spanning both halves
  if (
    leftSchedule &&
    rightSchedule &&
    leftSchedule.id === rightSchedule.id
  ) {
    const { subjectCode, colorCode } = getSubjectInfo(leftSchedule);
    return (
      <div className="time-slot-container">
        <div
          className="schedule merged"
          style={{ background: colorCode || "#FFF" }}
          onClick={() =>
            handleCellClick({
              type: "edit",
              schedule: leftSchedule,
              classId: cls.id,
              dayOfTheWeek: day.value,
              startTime: leftSchedule.startTime,
              endTime: leftSchedule.endTime,
            })
          }
        >
           {subjectCode}
        </div>
      </div>
    );
  }

  // Two half-slots
  return (
    <div className="time-slot-container">
      <div
        className={`half-slot ${leftSchedule ? "occupied" : "empty"}`}
        style={
          leftSchedule
            ? { background: getSubjectInfo(leftSchedule).colorCode || "#FFF" }
            : {}
        }
        onClick={() =>
          handleCellClick({
            type: leftSchedule ? "edit" : "add",
            schedule: leftSchedule || undefined,
            classId: cls.id,
            dayOfTheWeek: day.value,
            startTime: leftHalfStart,
            endTime: leftHalfEnd,
          })
        }
      >
        {leftSchedule ? getSubjectInfo(leftSchedule).subjectCode : "+"}
      </div>
      <div
        className={`half-slot ${rightSchedule ? "occupied" : "empty"}`}
        style={
          rightSchedule
            ? { background: getSubjectInfo(rightSchedule).colorCode || "#FFF" }
            : {}
        }
        onClick={() =>
          handleCellClick({
            type: rightSchedule ? "edit" : "add",
            schedule: rightSchedule || undefined,
            classId: cls.id,
            dayOfTheWeek: day.value,
            startTime: rightHalfStart,
            endTime: rightHalfEnd,
          })
        }
      >
        {rightSchedule ? getSubjectInfo(rightSchedule).subjectCode : "+"}
      </div>
    </div>
  );
};

export default CellContent;
